/**
 * LocalStorageDB
 * A simple local storage database that mimics Firestore's basic functionality
 */

const DB_PREFIX = 'localdb_';

/**
 * Get a document from the local storage database
 * @param {string} collection - The collection name
 * @param {string} id - The document ID
 * @returns {Object} - The document data and a exists() method
 */
export const getDoc = (collection, id) => {
  try {
    const key = `${collection}/${id}`;
    const data = localStorage.getItem(key);
    
    if (data) {
      console.log(`Document ${key} retrieved from local storage`);
      return JSON.parse(data);
    }
    
    console.log(`Document ${key} not found in local storage`);
    return null;
  } catch (error) {
    console.error(`Error retrieving document ${collection}/${id} from local storage:`, error);
    return null;
  }
};

/**
 * Set a document in the local storage database
 * @param {string} collection - The collection name
 * @param {string} id - The document ID
 * @param {Object} data - The document data
 */
export const setDoc = (collection, id, data) => {
  try {
    const key = `${collection}/${id}`;
    localStorage.setItem(key, JSON.stringify(data));
    console.log(`Document ${key} saved to local storage`);
    return true;
  } catch (error) {
    console.error(`Error saving document ${collection}/${id} to local storage:`, error);
    return false;
  }
};

/**
 * Update a document in the local storage database
 * @param {string} collection - The collection name
 * @param {string} id - The document ID
 * @param {Object} data - The document data to update
 */
export const updateDoc = (collection, id, data) => {
  const key = `${DB_PREFIX}${collection}_${id}`;
  const existingData = localStorage.getItem(key);
  
  if (existingData) {
    try {
      const doc = JSON.parse(existingData);
      const updatedDoc = {
        ...doc,
        ...data,
        _localUpdatedAt: new Date().toISOString()
      };
      
      localStorage.setItem(key, JSON.stringify(updatedDoc));
      return true;
    } catch (error) {
      console.error(`Error updating document ${key}:`, error);
      return false;
    }
  }
  
  return false;
};

/**
 * Delete a document from the local storage database
 * @param {string} collection - The collection name
 * @param {string} id - The document ID
 */
export const deleteDoc = (collection, id) => {
  const key = `${DB_PREFIX}${collection}_${id}`;
  localStorage.removeItem(key);
  return true;
};

/**
 * Get all documents in a collection
 * @param {string} collection - The collection name
 * @returns {Array} - Array of documents
 */
export const getCollection = (collection) => {
  const prefix = `${DB_PREFIX}${collection}_`;
  const docs = [];
  
  // Iterate through localStorage
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    
    if (key.startsWith(prefix)) {
      try {
        const id = key.substring(prefix.length);
        const data = JSON.parse(localStorage.getItem(key));
        
        docs.push({
          id,
          ...data
        });
      } catch (error) {
        console.error(`Error parsing data for ${key}:`, error);
      }
    }
  }
  
  return docs;
};

/**
 * Clear all data in the local storage database
 */
export const clearDatabase = () => {
  const keys = [];
  
  // Get all DB keys
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key.startsWith(DB_PREFIX)) {
      keys.push(key);
    }
  }
  
  // Remove all DB keys
  keys.forEach(key => localStorage.removeItem(key));
  
  return keys.length;
};

/**
 * Get stats about the local storage database
 */
export const getDatabaseStats = () => {
  const stats = {
    collections: {},
    totalDocuments: 0
  };
  
  // Iterate through localStorage
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    
    if (key.startsWith(DB_PREFIX)) {
      const parts = key.substring(DB_PREFIX.length).split('_');
      const collection = parts[0];
      
      if (!stats.collections[collection]) {
        stats.collections[collection] = 0;
      }
      
      stats.collections[collection]++;
      stats.totalDocuments++;
    }
  }
  
  return stats;
};

// Add this function to ensure we always have a fallback
export const ensureLocalBackup = (collection, id, data) => {
  try {
    const key = `${collection}/${id}`;
    localStorage.setItem(key, JSON.stringify(data));
    console.log(`Local backup created for ${key}`);
    return true;
  } catch (error) {
    console.error(`Failed to create local backup for ${collection}/${id}:`, error);
    return false;
  }
}; 